import type { VueQueryPluginOptions } from '@tanstack/vue-query'
import type { PrimeVueConfiguration } from 'primevue/config'
import type { Router } from 'vue-router'
import { RegleVuePlugin } from '@regle/core'
import * as Sentry from '@sentry/vue'
import { VueQueryPlugin } from '@tanstack/vue-query'
import { createPinia } from 'pinia'
import PrimeVue from 'primevue/config'
import ConfirmationService from 'primevue/confirmationservice'
import ToastService from 'primevue/toastservice'
import Tooltip from 'primevue/tooltip'
import { createApp, markRaw } from 'vue'
import App from '@/App.vue'
import SharedAsideCollapsible from '@/components/shared/SharedAsideCollapsible.vue'
import i18n from '@/i18n'
import enee from '@/presets/enee'
import router from '@/router'
import { generateUserRoleHierarchyMap } from '@/utils/authorization/role-hierarchy'
import '@/assets/css/global.css'

declare module 'pinia' {
  export interface PiniaCustomProperties {
    router: Router
  }
}

generateUserRoleHierarchyMap()

const pinia = createPinia()

pinia.use(({ store }) => {
  store.router = markRaw(router)
})

const primeVueConfiguration: PrimeVueConfiguration = {
  theme: {
    preset: enee,
    options: {
      prefix: 'p',
      darkModeSelector: '.app-dark',
      cssLayer: {
        name: 'primevue',
        order: 'reset, primevue, fonts, theme, utilities',
      },
    },
  },
}

const vueQueryPluginOptions: VueQueryPluginOptions = {
  queryClientConfig: {
    defaultOptions: {
      queries: {
        refetchOnWindowFocus: false,
      },
    },
  },
}

const app = createApp(App)

app.component('SharedAsideCollapsible', SharedAsideCollapsible)

app.use(pinia)
app.use(i18n)
app.use(router)

app.use(PrimeVue, primeVueConfiguration)

app.use(ConfirmationService)
app.use(ToastService)
app.directive('tooltip', Tooltip)

const sentryTraces = import.meta.env.MODE !== 'development'

if (sentryTraces) {
  Sentry.init({
    app,
    dsn: 'https://29dad0eca1280f0176f36a170a1b7e5a@o4505662917705728.ingest.sentry.io/4505663134892032',
    environment: import.meta.env.MODE,
    integrations: [
      Sentry.browserTracingIntegration({ router }),
      Sentry.replayIntegration(),
      Sentry.vueIntegration({
        tracingOptions: {
          timeout: 1000,
          trackComponents: true,
        },
      }),
    ],

    // Set tracesSampleRate to 1.0 to capture 100%
    // of transactions for performance monitoring.
    // We recommend adjusting this value in production
    tracesSampleRate: 0.2,

    // Set `tracePropagationTargets` to control for which URLs distributed tracing should be enabled
    tracePropagationTargets: [/^https:\/\/api\.staging\.enee\.io/, /^https:\/\/api\.enee\.io/],

    // Capture Replay for 100% of sessions with an error
    replaysOnErrorSampleRate: 1.0,
    // Capture Replay for 10% of all sessions
    replaysSessionSampleRate: 0.1,
  })
}

app.use(RegleVuePlugin)

app.use(VueQueryPlugin, vueQueryPluginOptions)

app.mount('#app')
